import React, { useState } from "react";
import {
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import { iconPathsMap } from "../../assets/icons/iconPaths";
import IconComponent from "./IconComponent";
import { useTranslation } from "react-i18next";

interface IconSelectProps {
  selectedIcon: string | null;
  onChange: (icon: string | null) => void;
  defaultIcon?: string;
}

const IconSelect: React.FC<IconSelectProps> = ({
  selectedIcon,
  onChange,
  defaultIcon,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  if (!iconPathsMap) return null;

  const currentIconPath =
    iconPathsMap.get((selectedIcon || defaultIcon) as any) || "";

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleIconClick = (key: string) => {
    onChange(key);
    handleClose();
  };

  return (
    <>
      <Stack direction='row' spacing={2} alignItems='center'>
        <Box
          onClick={handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            p: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.400",
            "&:hover": { backgroundColor: "grey.100" },
          }}
        >
          {selectedIcon ? (
            <>
              <IconComponent isSelected iconPath={currentIconPath} />
              <Typography
                variant='body1'
                sx={
                  {
                    color: theme.palette.natural.t1,
                    textWrap: "nowrap",
                  } as any
                }
              >
                {t("iconSelect.selectedIcon")}
              </Typography>
            </>
          ) : (
            <Typography
              variant='body1'
              sx={
                {
                  color: theme.palette.natural.t1,
                  textWrap: "nowrap",
                } as any
              }
            >
              {t("iconSelect.selectIcon")}
            </Typography>
          )}
        </Box>
      </Stack>

      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>{t("iconSelect.dialogTitle")}</DialogTitle>
        <DialogContent dividers style={{ maxHeight: "400px" }}>
          <Grid container spacing={2}>
            {Array.from(iconPathsMap).map(([key, value]) => (
              <Grid item xs={3} sm={2} key={key}>
                <IconComponent
                  isSelected={key === (selectedIcon || defaultIcon)}
                  iconPath={value}
                  onClick={() => handleIconClick(key)}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("iconSelect.dialogClose")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IconSelect;
